/**
 * Money is always an integer count of fils, the minor unit of the UAE dirham (1 AED = 100 fils).
 * Never a float, never a string, never a Decimal. Columns that hold money end in `_fils`.
 */

export const FILS_PER_AED = 100;

/** The dirham is pegged to the US dollar. For costs that suppliers bill in dollars (Twilio, Anthropic). */
export const AED_PER_USD = 3.6725;

export class MoneyError extends Error {}

function assertInteger(fils: number, label: string): void {
  if (!Number.isInteger(fils)) {
    throw new MoneyError(`${label} must be an integer number of fils, got ${fils}`);
  }
  if (!Number.isSafeInteger(fils)) {
    throw new MoneyError(`${label} is outside the safe integer range: ${fils}`);
  }
}

/** Convert a human amount in dirhams to fils. Rounds to the nearest fils. */
export function aedToFils(aed: number): number {
  if (!Number.isFinite(aed)) throw new MoneyError(`Not a finite amount: ${aed}`);
  return Math.round(aed * FILS_PER_AED);
}

/** A dollar amount from a supplier, in fils at the peg. Rounds to the nearest fils. Display only. */
export function usdToFils(usd: number): number {
  return aedToFils(usd * AED_PER_USD);
}

/** Convert fils to a floating point dirham amount. Display only, never arithmetic. */
export function filsToAed(fils: number): number {
  assertInteger(fils, "amount");
  return fils / FILS_PER_AED;
}

export function addFils(...amounts: number[]): number {
  let total = 0;
  for (const amount of amounts) {
    assertInteger(amount, "amount");
    total += amount;
  }
  return total;
}

export function subtractFils(a: number, b: number): number {
  assertInteger(a, "minuend");
  assertInteger(b, "subtrahend");
  return a - b;
}

/**
 * Apply a rate given in basis points (1 bps = 0.01%) and round half up.
 * A general helper for a part of an amount. The rounding rule is deliberate and tested: ties go
 * up. (Teamna takes no fee on payments, decision 3, 2026-09-25.)
 */
export function applyBps(amountFils: number, bps: number): number {
  assertInteger(amountFils, "amount");
  if (!Number.isInteger(bps)) throw new MoneyError(`Basis points must be an integer, got ${bps}`);
  if (bps < 0) throw new MoneyError(`Basis points must not be negative, got ${bps}`);
  return Math.round((amountFils * bps) / 10_000);
}

export type FormatAedOptions = {
  /** Drop ".00" on whole dirham amounts. Defaults to true, because prices are usually round. */
  trimZeroFils?: boolean;
  /** Symbol to use. Defaults to "AED" because the dirham glyph renders badly on older Androids. */
  symbol?: string;
};

/**
 * Format fils for display, for example 90000 becomes "AED 900" and 90050 becomes "AED 900.50".
 * Negative amounts render with the sign in front of the symbol: "-AED 25".
 */
export function formatAED(fils: number, options: FormatAedOptions = {}): string {
  assertInteger(fils, "amount");
  const { trimZeroFils = true, symbol = "AED" } = options;
  const negative = fils < 0;
  const absolute = Math.abs(fils);
  const dirhams = Math.floor(absolute / FILS_PER_AED);
  const remainder = absolute % FILS_PER_AED;
  const grouped = dirhams.toLocaleString("en-US");
  const body =
    remainder === 0 && trimZeroFils
      ? grouped
      : `${grouped}.${remainder.toString().padStart(2, "0")}`;
  return `${negative ? "-" : ""}${symbol} ${body}`;
}
